import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppHeader from "../components/AppHeader";
import { useTheme } from "../contexts/ThemeContext";
import { ArrowLeft, ArrowRight, RotateCcw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
 
// ─── Data & Pure Helpers (unchanged) ─────────────────────────────────────────
 
const PILLARS = [
    {
        key: "data",
        title: "Data Readiness",
        questions: [
            "Are core business systems integrated?",
            "Is operational data accessible without manual exports?",
            "Is there a centralized data warehouse or BI layer?",
            "Are dashboards used regularly for decision-making?",
            "Is data governance clearly defined?",
        ],
    },
    {
        key: "process",
        title: "Process Automation",
        questions: [
            "Are key workflows documented?",
            "Are repetitive tasks automated today?",
            "Are approvals and routing automated?",
            "Do teams spend significant time on manual repetitive work?",
            "Are automation tools actively used?",
        ],
    },
    {
        key: "tech",
        title: "Technology Infrastructure",
        questions: [
            "Does the company use modern cloud platforms?",
            "Are APIs available for core systems?",
            "Are AI tools already in use internally?",
            "Is the architecture integration-friendly?",
            "Are identity and security systems centralized?",
        ],
    },
    {
        key: "workforce",
        title: "Workforce AI Readiness",
        questions: [
            "Are employees using AI tools today?",
            "Has leadership defined an AI strategy?",
            "Are employees trained on AI tools?",
            "Are teams encouraged to experiment with AI?",
            "Is there an internal AI champion or team?",
        ],
    },
    {
        key: "governance",
        title: "AI Governance",
        questions: [
            "Are AI usage policies defined?",
            "Are AI privacy and compliance risks monitored?",
            "Are regulatory requirements integrated into governance?",
            "Is there transparency into AI-supported decisions?",
            "Does leadership review AI initiatives regularly?",
        ],
    },
    {
        key: "useCases",
        title: "AI Use Cases",
        questions: [
            "Is AI used in customer support?",
            "Is AI used in sales or marketing?",
            "Is AI used in analytics or reporting?",
            "Are automation tools deployed internally?",
            "Is ROI from AI initiatives tracked?",
        ],
    },
];
 
const SCORE_OPTIONS = [0, 1, 2, 3, 4, 5];
const MAX_SCORE = 150;
 
const EMPTY_COMPANY = {
    companyName: "",
    industry: "",
    title: "",
    employees: "",
    contactName: "",
    email: "",
};
 
function buildInitialScores() {
    const map = {};
    PILLARS.forEach((pillar) => {
        pillar.questions.forEach((_, index) => {
            map[`${pillar.key}-${index}`] = 0;
        });
    });
    return map;
}
 
function getMaturity(totalScore) {
    if (totalScore < 40) return "AI Unprepared";
    if (totalScore < 80) return "Early AI Adoption";
    if (totalScore < 120) return "AI Enabled";
    return "AI First Organization";
}
 
function getMaturityColors(maturity, isDark) {
    const map = {
        "AI Unprepared":         { badge: isDark ? "rgba(239,68,68,0.15)"  : "#fee2e2", text: isDark ? "#f87171" : "#b91c1c", border: isDark ? "rgba(239,68,68,0.3)"  : "#fca5a5" },
        "Early AI Adoption":     { badge: isDark ? "rgba(245,158,11,0.15)" : "#fef3c7", text: isDark ? "#fbbf24" : "#92400e", border: isDark ? "rgba(245,158,11,0.3)" : "#fde68a" },
        "AI Enabled":            { badge: isDark ? "rgba(59,130,246,0.15)" : "#dbeafe", text: isDark ? "#60a5fa" : "#1d4ed8", border: isDark ? "rgba(59,130,246,0.3)" : "#93c5fd" },
        "AI First Organization": { badge: isDark ? "rgba(16,185,129,0.15)": "#d1fae5", text: isDark ? "#34d399" : "#065f46", border: isDark ? "rgba(16,185,129,0.3)": "#6ee7b7" },
    };
    return map[maturity] || { badge: "rgba(100,116,139,0.12)", text: "#64748b", border: "rgba(100,116,139,0.2)" };
}
 
function getPillarBarColor(total) {
    if (total <= 8)  return "#ef4444";
    if (total <= 15) return "#f59e0b";
    if (total <= 20) return "#3b82f6";
    return "#10b981";
}
 
function getRecommendationMap() {
    return {
        data:      { focus: "Data & Analytics Pilot",         agents: ["BI Data Storyteller Agent", "Data Query Agent", "Reporting Automation Agent"],    impact: "Create a usable data foundation, reduce manual reporting, and unlock faster executive insight." },
        process:   { focus: "Operations Automation Pilot",    agents: ["Workflow Automation Agent", "Approval Workflow Agent", "SLA Monitoring Agent"],    impact: "Reduce manual workflow friction and automate repetitive internal tasks." },
        tech:      { focus: "AI Foundation Pilot",            agents: ["Integration Agent", "Access Governance Agent", "Workflow Orchestration Agent"],    impact: "Establish the technical foundation needed to scale AI safely across the business." },
        workforce: { focus: "AI Enablement Pilot",            agents: ["Employee Support Agent", "Knowledge Agent", "Training Recommendation Agent"],      impact: "Increase internal adoption, AI fluency, and organizational readiness." },
        governance:{ focus: "AI Governance Pilot",            agents: ["Audit Monitoring Agent", "Policy Compliance Agent", "Access Governance Agent"],    impact: "Improve AI oversight, compliance posture, and decision transparency." },
        useCases:  { focus: "Customer Support & Sales Pilot", agents: ["Customer Support Agent", "Lead Qualification Agent", "Sales Outreach Agent"],      impact: "Prove near-term AI value in customer-facing workflows and create measurable ROI." },
    };
}
 
// ─── Card token helper ────────────────────────────────────────────────────────
function card(isDark) {
    return {
        background: isDark ? "#0f1729" : "#ffffff",
        border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.07)"}`,
        boxShadow: isDark ? "0 2px 8px rgba(0,0,0,0.4)" : "0 1px 6px rgba(0,0,0,0.04)",
    };
}
 
// ─── Ghost button style helper ────────────────────────────────────────────────
function ghostBtn(isDark) {
    return {
        background: isDark ? "rgba(255,255,255,0.05)" : "#f1f5f9",
        border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.07)"}`,
        color: isDark ? "#c9d1d9" : "#475569",
    };
}
 
// ─── Executive Summary Page ───────────────────────────────────────────────────
function ExecutiveSummaryPage({ company, pillarTotals, totalScore, weakest, recommendation, onBack, onReset, isDark }) {
    const percent = Math.round((totalScore / MAX_SCORE) * 100);
    const maturity = getMaturity(totalScore);
    const mc = getMaturityColors(maturity, isDark);
 
    const strongest = useMemo(() => [...pillarTotals].sort((a, b) => b.total - a.total)[0], [pillarTotals]);
 
    const hover = (e, on) => {
        e.currentTarget.style.borderColor = on ? "rgba(248,114,23,0.4)" : (isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.07)");
        e.currentTarget.style.color       = on ? "#F87217"              : (isDark ? "#c9d1d9" : "#475569");
    };
 
    return (
        <div className="min-h-screen" style={{ background: isDark ? "#0a0f1e" : "#f8fafc" }}>
            <AppHeader />
 
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 py-6 space-y-4">
 
                {/* Title bar */}
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={onBack}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm font-semibold transition-all duration-200"
                            style={ghostBtn(isDark)}
                            onMouseEnter={e => hover(e, true)}
                            onMouseLeave={e => hover(e, false)}
                        >
                            <ArrowLeft className="w-3.5 h-3.5" /> Back
                        </button>
                        <div>
                            <p className="text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color: "#F87217" }}>Netsmartz</p>
                            <h1 className="text-lg font-bold leading-tight" style={{ fontFamily: "'Outfit',sans-serif", color: isDark ? "#f0f6fc" : "#0d1a3a" }}>Executive Summary Report</h1>
                        </div>
                    </div>
                    <button
                        onClick={onReset}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm font-semibold transition-all duration-200"
                        style={ghostBtn(isDark)}
                        onMouseEnter={e => hover(e, true)}
                        onMouseLeave={e => hover(e, false)}
                    >
                        <RotateCcw className="w-3.5 h-3.5" /> New Assessment
                    </button>
                </div>
 
                {/* Hero: Company + Score */}
                <div className="rounded-2xl p-5 md:p-6" style={card(isDark)}>
                    <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
                        <div>
                            <p className="text-xs mb-0.5" style={{ color: isDark ? "#6e7681" : "#94a3b8" }}>Assessment prepared for</p>
                            <h2 className="text-2xl font-bold" style={{ fontFamily: "'Outfit',sans-serif", color: isDark ? "#f0f6fc" : "#0d1a3a" }}>{company.companyName || "Your Organization"}</h2>
                            <div className="mt-2 flex flex-wrap gap-1.5">
                                {[company.industry, company.employees && `${company.employees} employees`, company.contactName && `Contact: ${company.contactName}`, company.title].filter(Boolean).map((tag) => (
                                    <span key={tag} className="rounded-full px-2.5 py-0.5 text-xs font-medium" style={{ background: isDark ? "rgba(255,255,255,0.07)" : "#f1f5f9", color: isDark ? "#c9d1d9" : "#475569" }}>{tag}</span>
                                ))}
                            </div>
                        </div>
                        <div className="flex items-center gap-5 flex-shrink-0">
                            {/* Circular score */}
                            <div className="relative flex h-24 w-24 items-center justify-center">
                                <svg className="absolute inset-0 -rotate-90" viewBox="0 0 120 120">
                                    <circle cx="60" cy="60" r="52" fill="none" stroke={isDark ? "rgba(255,255,255,0.07)" : "#e2e8f0"} strokeWidth="10" />
                                    <circle cx="60" cy="60" r="52" fill="none" stroke="#F87217" strokeWidth="10"
                                        strokeDasharray={`${2 * Math.PI * 52}`}
                                        strokeDashoffset={`${2 * Math.PI * 52 * (1 - percent / 100)}`}
                                        strokeLinecap="round" className="transition-all duration-700" />
                                </svg>
                                <div className="text-center">
                                    <p className="text-xl font-bold" style={{ color: isDark ? "#f0f6fc" : "#0d1a3a" }}>{percent}%</p>
                                    <p className="text-[10px]" style={{ color: isDark ? "#6e7681" : "#94a3b8" }}>Ready</p>
                                </div>
                            </div>
                            <div className="space-y-1.5">
                                <div>
                                    <p className="text-[10px] uppercase tracking-wide" style={{ color: isDark ? "#6e7681" : "#94a3b8" }}>Total Score</p>
                                    <p className="text-2xl font-bold" style={{ color: isDark ? "#f0f6fc" : "#0d1a3a" }}>{totalScore}<span className="text-sm font-normal" style={{ color: isDark ? "#6e7681" : "#94a3b8" }}>/{MAX_SCORE}</span></p>
                                </div>
                                <span className="inline-block rounded-full px-3 py-1 text-xs font-semibold" style={{ background: mc.badge, color: mc.text }}>{maturity}</span>
                            </div>
                        </div>
                    </div>
                </div>
 
                {/* Pillar Breakdown */}
                <div className="rounded-2xl p-5 md:p-6" style={card(isDark)}>
                    <h2 className="text-base font-bold mb-0.5" style={{ fontFamily: "'Outfit',sans-serif", color: isDark ? "#f0f6fc" : "#0d1a3a" }}>Pillar Breakdown</h2>
                    <p className="text-xs mb-4" style={{ color: isDark ? "#6e7681" : "#94a3b8" }}>Score breakdown across all six AI readiness pillars.</p>
                    <div className="space-y-3">
                        {pillarTotals.map((pillar) => {
                            const pct = Math.round((pillar.total / 25) * 100);
                            const isWeakest  = pillar.key === weakest?.key;
                            const isStrongest = pillar.key === strongest?.key;
                            return (
                                <div key={pillar.key} className="rounded-xl p-3" style={{
                                    background: isWeakest ? (isDark ? "rgba(239,68,68,0.07)"   : "#fff5f5") : isStrongest ? (isDark ? "rgba(16,185,129,0.07)"  : "#f0fdf4") : (isDark ? "rgba(255,255,255,0.03)" : "#fafafa"),
                                    border: `1px solid ${isWeakest ? (isDark ? "rgba(239,68,68,0.2)" : "#fecaca") : isStrongest ? (isDark ? "rgba(16,185,129,0.2)" : "#bbf7d0") : (isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0")}`,
                                }}>
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-semibold" style={{ color: isDark ? "#c9d1d9" : "#1e293b" }}>{pillar.title}</span>
                                            {isWeakest   && <span className="rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ background: "rgba(239,68,68,0.12)",  color: "#ef4444" }}>Weakest</span>}
                                            {isStrongest && <span className="rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ background: "rgba(16,185,129,0.12)", color: "#10b981" }}>Strongest</span>}
                                        </div>
                                        <span className="text-xs font-bold" style={{ color: isDark ? "#c9d1d9" : "#374151" }}>{pillar.total}/25 <span style={{ color: isDark ? "#6e7681" : "#94a3b8", fontWeight: 400 }}>({pct}%)</span></span>
                                    </div>
                                    <div className="h-2 rounded-full" style={{ background: isDark ? "rgba(255,255,255,0.07)" : "#e2e8f0" }}>
                                        <div className="h-2 rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: getPillarBarColor(pillar.total) }} />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
 
                {/* Maturity + Pilot Recommendation */}
                <div className="grid gap-4 lg:grid-cols-2">
                    <div className="rounded-2xl p-5" style={{ ...card(isDark), border: `1px solid ${mc.border}` }}>
                        <h2 className="text-base font-bold mb-0.5" style={{ fontFamily: "'Outfit',sans-serif", color: isDark ? "#f0f6fc" : "#0d1a3a" }}>Maturity Assessment</h2>
                        <p className="text-xs mb-4" style={{ color: isDark ? "#6e7681" : "#94a3b8" }}>Current AI organizational maturity level.</p>
                        <span className="inline-block rounded-xl px-4 py-2 text-sm font-bold mb-4" style={{ background: mc.badge, color: mc.text }}>{maturity}</span>
                        <div className="space-y-1.5">
                            {[
                                { label: "AI Unprepared",         range: "0–39",    active: totalScore < 40 },
                                { label: "Early AI Adoption",     range: "40–79",   active: totalScore >= 40 && totalScore < 80 },
                                { label: "AI Enabled",            range: "80–119",  active: totalScore >= 80 && totalScore < 120 },
                                { label: "AI First Organization", range: "120–150", active: totalScore >= 120 },
                            ].map((level) => (
                                <div key={level.label} className="flex items-center justify-between rounded-xl px-3 py-2 text-xs" style={{
                                    background: level.active ? (isDark ? "rgba(248,114,23,0.1)" : "#fff") : "transparent",
                                    fontWeight: level.active ? 700 : 400,
                                    color: level.active ? (isDark ? "#f0f6fc" : "#0d1a3a") : (isDark ? "#6e7681" : "#94a3b8"),
                                    boxShadow: level.active ? (isDark ? "0 1px 4px rgba(0,0,0,0.4)" : "0 1px 4px rgba(0,0,0,0.05)") : "none",
                                }}>
                                    <span>{level.label}</span><span>{level.range}</span>
                                </div>
                            ))}
                        </div>
                    </div>
 
                    <div className="rounded-2xl p-5" style={{ background: "#0d1a3a", boxShadow: "0 2px 8px rgba(0,0,0,0.4)" }}>
                        <h2 className="text-base font-bold text-white" style={{ fontFamily: "'Outfit',sans-serif" }}>Pilot Recommendation</h2>
                        <p className="mt-0.5 text-xs text-slate-400">Based on the lowest-scoring pillar.</p>
                        <div className="mt-4 rounded-xl p-4" style={{ background: "rgba(248,114,23,0.12)", border: "1px solid rgba(248,114,23,0.25)" }}>
                            <p className="text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color: "#F87217" }}>Recommended Pilot Focus</p>
                            <p className="mt-1 text-lg font-bold text-white">{recommendation?.focus || "—"}</p>
                            <p className="mt-1.5 text-xs leading-relaxed text-slate-300">{recommendation?.impact}</p>
                        </div>
                        <div className="mt-4">
                            <p className="text-xs font-semibold mb-2" style={{ color: "#F87217" }}>Suggested Agents</p>
                            <div className="space-y-1.5">
                                {(recommendation?.agents || []).map((agent) => (
                                    <div key={agent} className="rounded-xl px-3 py-2 text-xs font-medium text-white flex items-center gap-2" style={{ border: "1px solid rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.05)" }}>
                                        <span className="h-1.5 w-1.5 rounded-full flex-shrink-0" style={{ background: "#F87217" }} />{agent}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
 
                {/* Next Steps */}
                <div className="rounded-2xl p-5 md:p-6" style={card(isDark)}>
                    <h2 className="text-base font-bold mb-0.5" style={{ fontFamily: "'Outfit',sans-serif", color: isDark ? "#f0f6fc" : "#0d1a3a" }}>Proposed Next Steps</h2>
                    <p className="text-xs mb-4" style={{ color: isDark ? "#6e7681" : "#94a3b8" }}>An 8-week AI Workforce Pilot focused on one high-impact business area.</p>
                    <div className="grid gap-3 md:grid-cols-3">
                        <div className="rounded-xl p-4" style={{ background: isDark ? "rgba(248,114,23,0.08)" : "#fff7ed", border: `1px solid ${isDark ? "rgba(248,114,23,0.2)" : "#fed7aa"}` }}>
                            <p className="text-[10px] font-bold uppercase tracking-wide mb-1" style={{ color: "#F87217" }}>Pilot Duration</p>
                            <p className="text-2xl font-bold" style={{ color: isDark ? "#f0f6fc" : "#0d1a3a" }}>8 Weeks</p>
                            <p className="mt-1 text-xs" style={{ color: isDark ? "#c9d1d9" : "#475569" }}>Focused, time-boxed engagement with clear milestones.</p>
                        </div>
                        <div className="rounded-xl p-4" style={{ background: "#0d1a3a" }}>
                            <p className="text-[10px] font-bold uppercase tracking-wide mb-1 text-slate-400">Commercial Range</p>
                            <p className="text-2xl font-bold text-white">$50K–$120K</p>
                            <p className="mt-1 text-xs text-slate-400">Scoped to pilot depth and agent complexity.</p>
                        </div>
                        <div className="rounded-xl p-4" style={{ background: isDark ? "rgba(16,185,129,0.08)" : "#f0fdf4", border: `1px solid ${isDark ? "rgba(16,185,129,0.2)" : "#bbf7d0"}` }}>
                            <p className="text-[10px] font-bold uppercase tracking-wide mb-1" style={{ color: "#10b981" }}>Goal</p>
                            <p className="text-lg font-bold" style={{ color: isDark ? "#f0f6fc" : "#065f46" }}>Measurable ROI</p>
                            <p className="mt-1 text-xs" style={{ color: isDark ? "#c9d1d9" : "#475569" }}>Validate gains and define enterprise rollout roadmap.</p>
                        </div>
                    </div>
                </div>
 
                {/* Footer */}
                <div className="flex flex-wrap gap-3 pb-8">
                    <button
                        onClick={onBack}
                        className="rounded-xl px-5 py-2.5 text-sm font-semibold transition-all duration-200"
                        style={ghostBtn(isDark)}
                        onMouseEnter={e => hover(e, true)}
                        onMouseLeave={e => hover(e, false)}
                    >← Edit Scorecard</button>
                    <button
                        onClick={onReset}
                        className="group flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold text-white transition-all duration-200"
                        style={{ background: "linear-gradient(135deg,#F87217,#FF9A4E)", boxShadow: "0 4px 16px rgba(248,114,23,0.3)" }}
                        onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 8px 24px rgba(248,114,23,0.45)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
                        onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 4px 16px rgba(248,114,23,0.3)"; e.currentTarget.style.transform = "translateY(0)"; }}
                    >Start New Assessment</button>
                </div>
            </div>
        </div>
    );
}
 
// ─── Main Scorecard Tool ──────────────────────────────────────────────────────
export default function AIAdoptionScorecardTool() {
    const { isDark } = useTheme();
    const navigate   = useNavigate();
 
    const [company, setCompany] = useState(EMPTY_COMPANY);
    const [scores,  setScores]  = useState(() => buildInitialScores());
    const [view,    setView]    = useState("scorecard");
 
    const pillarTotals = useMemo(() => PILLARS.map((pillar) => {
        const total = pillar.questions.reduce((sum, _, i) => sum + Number(scores[`${pillar.key}-${i}`] || 0), 0);
        const answered = pillar.questions.filter((_, i) => Number(scores[`${pillar.key}-${i}`] || 0) > 0).length;
        return { ...pillar, total, answered };
    }), [scores]);
 
    const totalScore       = useMemo(() => pillarTotals.reduce((sum, p) => sum + p.total, 0), [pillarTotals]);
    const totalAnswered    = useMemo(() => pillarTotals.reduce((sum, p) => sum + p.answered, 0), [pillarTotals]);
    const fillPercent      = Math.round((totalAnswered / 30) * 100);
    const percent          = Math.round((totalScore / MAX_SCORE) * 100);
    const maturity         = getMaturity(totalScore);
    const weakest          = useMemo(() => pillarTotals.length ? [...pillarTotals].sort((a, b) => a.total - b.total)[0] : null, [pillarTotals]);
    const recommendation   = weakest ? getRecommendationMap()[weakest.key] : null;
 
    const handleScoreChange   = (key, value) => setScores(prev => ({ ...prev, [key]: Number(value) }));
    const updateCompanyField  = (field, value) => setCompany(prev => ({ ...prev, [field]: value }));
    const resetAll            = () => { setCompany(EMPTY_COMPANY); setScores(buildInitialScores()); setView("scorecard"); };
    const handleSubmit        = (e) => {
        e.preventDefault();
        setView("summary");
        window.scrollTo({ top: 0, behavior: "smooth" });
        // Fire-and-forget: persist result to backend
        const pillarMap = {};
        pillarTotals.forEach(p => { pillarMap[p.key] = p.total; });
        const payload = {
            company_name: company.companyName,
            industry: company.industry,
            title: company.title,
            employees: company.employees || null,
            contact_name: company.contactName,
            email: company.email,
            total_score: totalScore,
            readiness_pct: Math.round((totalScore / MAX_SCORE) * 100),
            maturity,
            score_data: pillarMap["data"] ?? 0,
            score_process: pillarMap["process"] ?? 0,
            score_tech: pillarMap["tech"] ?? 0,
            score_workforce: pillarMap["workforce"] ?? 0,
            score_governance: pillarMap["governance"] ?? 0,
            score_use_cases: pillarMap["useCases"] ?? 0,
            weakest_pillar: weakest?.key ?? null,
            recommendation_focus: recommendation?.focus ?? null,
            recommendation_impact: recommendation?.impact ?? null,
            recommendation_agents: recommendation?.agents ?? [],
        };
        fetch(`${API}/scorecard/results`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        }).catch(() => {/* silent — don't block UX */});
    };
 
    if (view === "summary") {
        return <ExecutiveSummaryPage company={company} pillarTotals={pillarTotals} totalScore={totalScore} weakest={weakest} recommendation={recommendation} onBack={() => setView("scorecard")} onReset={resetAll} isDark={isDark} />;
    }
 
    const hover = (e, on) => {
        e.currentTarget.style.borderColor = on ? "rgba(248,114,23,0.4)"                      : (isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)");
        e.currentTarget.style.color       = on ? "#F87217"                                    : (isDark ? "#c9d1d9" : "#374151");
    };
 
    return (
        <div className="min-h-screen" style={{ background: isDark ? "#0a0f1e" : "#f8fafc" }}>
            <AppHeader />
 
            <form onSubmit={handleSubmit}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 py-6 md:py-8 space-y-5">
 
                {/* ── Page Header ── */}
                <div className="rounded-2xl p-5" style={card(isDark)}>
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                        <div>
                            <button
                                type="button"
                                onClick={() => navigate("/dashboard")}
                                className="flex items-center gap-1 text-xs font-semibold mb-2 transition-colors duration-200"
                                style={{ color: isDark ? "#6e7681" : "#94a3b8" }}
                                onMouseEnter={e => e.currentTarget.style.color = "#F87217"}
                                onMouseLeave={e => e.currentTarget.style.color = isDark ? "#6e7681" : "#94a3b8"}
                            >
                                <ArrowLeft className="w-3.5 h-3.5" /> Dashboard
                            </button>
 
                            <h1 className="text-2xl md:text-3xl font-extrabold" style={{ fontFamily: "'Outfit',sans-serif", letterSpacing: "-0.02em", color: isDark ? "#f0f6fc" : "#0d1a3a" }}>AI Adoption Scorecard</h1>
                            <p className="mt-1 text-sm max-w-2xl" style={{ color: isDark ? "#8b949e" : "#475569" }}>
                                Evaluate how ready an organization is to deploy an AI workforce across operations, sales, customer service, analytics, and governance.
                            </p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
                            <MiniStat label="Form Filled" value={`${fillPercent}%`} isDark={isDark} />
                            <button
                                type="button" onClick={resetAll}
                                className="flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-semibold transition-all duration-200"
                                style={{ background: isDark ? "rgba(255,255,255,0.05)" : "#f8fafc", border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)"}`, color: isDark ? "#c9d1d9" : "#374151" }}
                                onMouseEnter={e => hover(e, true)}
                                onMouseLeave={e => hover(e, false)}
                            >
                                <RotateCcw className="w-3.5 h-3.5" /> Reset
                            </button>
                        </div>
                    </div>
                </div>
 
                {/* ── Top Row: [Company Profile + Next Step] | [Live Summary] ── */}
                <div className="grid gap-5 xl:grid-cols-[1fr_380px]">
 
                    {/* Left column: Company Profile + Next Step stacked */}
                    <div className="space-y-4">
                        <section className="rounded-2xl p-5" style={card(isDark)}>
                            <h2 className="text-base font-bold mb-0.5" style={{ fontFamily: "'Outfit',sans-serif", color: isDark ? "#f0f6fc" : "#0d1a3a" }}>Company Profile</h2>
                            <p className="text-sm mb-5" style={{ color: isDark ? "#8b949e" : "#475569" }}>Capture the account context before scoring.</p>
                            <div className="grid gap-4 grid-cols-1 sm:grid-cols-2">
                                <Input label="Company Name"      value={company.companyName}  onChange={e => updateCompanyField("companyName",  e.target.value)} isDark={isDark} required />
                                <Input label="Industry"          value={company.industry}     onChange={e => updateCompanyField("industry",     e.target.value)} isDark={isDark} required />
                                <Input label="Title"             value={company.title}        onChange={e => updateCompanyField("title",        e.target.value)} isDark={isDark} required />
                                <Input label="Employees"         value={company.employees}    onChange={e => updateCompanyField("employees",    e.target.value)} isDark={isDark} />
                                <Input label="Contact Name"      value={company.contactName}  onChange={e => updateCompanyField("contactName",  e.target.value)} isDark={isDark} required />
                                <Input label="Email" type="email" value={company.email}       onChange={e => updateCompanyField("email",        e.target.value)} isDark={isDark} required />
                            </div>
                        </section>
                        <section className="rounded-2xl p-4" style={card(isDark)}>
                            <h2 className="text-sm font-bold mb-0.5" style={{ fontFamily: "'Outfit',sans-serif", color: isDark ? "#f0f6fc" : "#0d1a3a" }}>Next Step</h2>
                            <p className="text-xs mb-3" style={{ color: isDark ? "#8b949e" : "#475569" }}>8-week AI Workforce Pilot with measurable ROI.</p>
                            <div className="rounded-xl p-3 space-y-1.5 text-xs" style={{ background: isDark ? "rgba(255,255,255,0.03)" : "#f8fafc", border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}` }}>
                                {[["Pilot Duration", "8 weeks"], ["Commercial Range", "$50K\u2013$120K"], ["Goal", "Validate gains and define the enterprise rollout roadmap."]].map(([k, v]) => (
                                    <p key={k} style={{ color: isDark ? "#c9d1d9" : "#374151" }}><span className="font-semibold">{k}:</span> {v}</p>
                                ))}
                            </div>
                        </section>
                    </div>
 
                    {/* ── Sticky Live Sidebar ── */}
                    <div className="xl:sticky xl:top-20 h-full">
 
                        <section className="rounded-2xl p-5 h-full flex flex-col" style={card(isDark)}>
                            <h2 className="text-base font-bold mb-0.5" style={{ fontFamily: "'Outfit',sans-serif", color: isDark ? "#f0f6fc" : "#0d1a3a" }}>Live Summary</h2>
                            <p className="text-xs mb-4" style={{ color: isDark ? "#8b949e" : "#475569" }}>Updated in real-time as you score each pillar.</p>
                            <div className="flex-1 flex flex-col justify-between">
                                {pillarTotals.map((pillar) => (
                                    <div key={pillar.key}>
                                        <div className="mb-1 flex items-center justify-between text-xs font-medium">
                                            <span style={{ color: isDark ? "#c9d1d9" : "#374151" }}>{pillar.title}</span>
                                            <span style={{ color: isDark ? "#8b949e" : "#64748b" }}>{pillar.answered}/5 filled</span>
                                        </div>
                                        <div className="h-1.5 rounded-full" style={{ background: isDark ? "rgba(255,255,255,0.07)" : "#e2e8f0" }}>
                                            <div className="h-1.5 rounded-full transition-all duration-300" style={{ width: `${(pillar.answered / 5) * 100}%`, background: "linear-gradient(90deg,#F87217,#FF9A4E)" }} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>
                    </div>
                </div>
 
                {/* ── Pilot Recommendation — full width, focus left / agents right ── */}
                <section className="rounded-2xl p-5" style={{ background: isDark ? "#ffffff" : "#0d1a3a", boxShadow: isDark ? "0 2px 8px rgba(0,0,0,0.08)" : "0 2px 8px rgba(0,0,0,0.4)" }}>
                    <h2 className="text-base font-bold mb-0.5" style={{ fontFamily: "'Outfit',sans-serif", color: isDark ? "#0d1a3a" : "#ffffff" }}>Pilot Recommendation</h2>
                    <p className="text-xs mb-4" style={{ color: isDark ? "#475569" : "#94a3b8" }}>Based on the lowest-scoring pillar.</p>
                    <div className="grid gap-5 md:grid-cols-2">
                        {/* Left: Recommended Focus */}
                        <div className="rounded-xl p-4" style={{ background: "rgba(248,114,23,0.12)", border: "1px solid rgba(248,114,23,0.25)" }}>
                            <p className="text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color: "#F87217" }}>Recommended Focus</p>
                            <p className="mt-1.5 text-lg font-bold" style={{ color: isDark ? "#0d1a3a" : "#ffffff" }}>{recommendation?.focus || "—"}</p>
                            <p className="mt-2 text-xs leading-relaxed" style={{ color: isDark ? "#374151" : "#cbd5e1" }}>{recommendation?.impact || "Score the pillars to see a recommendation."}</p>
                        </div>
                        {/* Right: Suggested Agents */}
                        <div>
                            <p className="text-xs font-semibold mb-3" style={{ color: "#F87217" }}>Suggested Agents</p>
                            <div className="space-y-2">
                                {(recommendation?.agents || []).map((agent) => (
                                    <div key={agent} className="rounded-xl px-3 py-2.5 text-sm font-medium flex items-center gap-2.5"
                                        style={{
                                            border: `1px solid ${isDark ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.1)"}`,
                                            background: isDark ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.05)",
                                            color: isDark ? "#0d1a3a" : "#ffffff"
                                        }}>
                                        <span className="h-2 w-2 rounded-full flex-shrink-0" style={{ background: "#F87217" }} />{agent}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>
 
 
                {/* ── Pillar Grid: full-width, 2 columns ── */}
 
                <div>
                    <div className="mb-4">
                        <h2 className="text-lg font-bold" style={{ fontFamily: "'Outfit',sans-serif", color: isDark ? "#f0f6fc" : "#0d1a3a" }}>AI Readiness Pillars</h2>
                        <p className="text-sm mt-0.5" style={{ color: isDark ? "#8b949e" : "#475569" }}>Score each question from 0 (not done) to 5 (fully implemented).</p>
                    </div>
 
                    <div className="grid gap-5 md:grid-cols-2">
                        {PILLARS.map((pillar, pillarIndex) => {
                            const pillarData = pillarTotals.find(p => p.key === pillar.key);
                            const pillarTotal = pillarData?.total || 0;
                            const pillarAnswered = pillarData?.answered || 0;
                            const pct = Math.round((pillarAnswered / 5) * 100);
                            return (
                                <section key={pillar.key} className="rounded-2xl p-5" style={card(isDark)}>
                                    <div className="mb-3 flex items-start justify-between gap-3">
                                        <div>
                                            <h2 className="text-base font-bold" style={{ fontFamily: "'Outfit',sans-serif", color: isDark ? "#f0f6fc" : "#0d1a3a" }}>
                                                {pillarIndex + 1}. {pillar.title}
                                            </h2>
                                            <p className="text-xs mt-0.5" style={{ color: isDark ? "#8b949e" : "#94a3b8" }}>Rate 0 – 5 per question</p>
                                        </div>
                                        <div className="text-right flex-shrink-0">
                                            <div className="rounded-lg px-3 py-1.5 text-sm font-bold" style={{ background: isDark ? "rgba(248,114,23,0.1)" : "rgba(248,114,23,0.08)", color: "#F87217", border: "1px solid rgba(248,114,23,0.2)" }}>
                                                {pillarAnswered}/5
                                            </div>
                                            <p className="text-[10px] mt-1" style={{ color: isDark ? "#6e7681" : "#94a3b8" }}>{pct}% filled</p>
                                        </div>
                                    </div>
                                    <div className="h-1.5 rounded-full mb-4" style={{ background: isDark ? "rgba(255,255,255,0.07)" : "#e2e8f0" }}>
                                        <div className="h-1.5 rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: getPillarBarColor(pillarTotal) }} />
                                    </div>
                                    <div className="space-y-3">
                                        {pillar.questions.map((question, index) => {
                                            const scoreKey = `${pillar.key}-${index}`;
                                            const activeVal = Number(scores[scoreKey]);
                                            return (
                                                <div key={scoreKey} className="rounded-xl p-4" style={{ border: `1px solid ${isDark ? "rgba(255,255,255,0.07)" : "#e2e8f0"}`, background: isDark ? "rgba(255,255,255,0.02)" : "#fafafa" }}>
                                                    <div className="mb-3 flex items-start justify-between gap-3">
                                                        <p className="text-sm font-medium leading-snug" style={{ color: isDark ? "#c9d1d9" : "#1e293b" }}>{question}</p>
                                                        <span className="rounded-full px-2 py-0.5 text-[10px] font-bold flex-shrink-0 mt-0.5" style={{ background: isDark ? "rgba(255,255,255,0.07)" : "#f1f5f9", color: isDark ? "#8b949e" : "#64748b" }}>
                                                            Q{pillarIndex * 5 + index + 1}
                                                        </span>
                                                    </div>
                                                    <div className="flex gap-2">
                                                        {SCORE_OPTIONS.map((option) => {
                                                            const isActive = activeVal === option;
                                                            return (
                                                                <button
                                                                    key={option} type="button"
                                                                    onClick={() => handleScoreChange(scoreKey, option)}
                                                                    className="h-9 flex-1 rounded-lg text-sm font-bold transition-all duration-150"
                                                                    style={{
                                                                        background: isActive ? "#F87217" : (isDark ? "rgba(255,255,255,0.05)" : "#fff"),
                                                                        border: `1px solid ${isActive ? "#F87217" : (isDark ? "rgba(255,255,255,0.12)" : "#d1d5db")}`,
                                                                        color: isActive ? "#fff" : (isDark ? "#c9d1d9" : "#374151"),
                                                                        boxShadow: isActive ? "0 2px 8px rgba(248,114,23,0.3)" : "none",
                                                                    }}
                                                                    aria-pressed={isActive}
                                                                    aria-label={`Score ${option} for ${question}`}
                                                                >
                                                                    {option}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </section>
                            );
                        })}
                    </div>
                </div>
 
                {/* ── Submit/Reset ── */}
                <div className="flex flex-wrap gap-3 pb-6">
                    <button
                        type="submit"
                        className="group flex items-center gap-2 rounded-xl px-6 py-3 text-sm font-semibold text-white transition-all duration-200"
                        style={{ background: "linear-gradient(135deg,#F87217,#FF9A4E)", boxShadow: "0 4px 16px rgba(248,114,23,0.3)" }}
                        onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 8px 24px rgba(248,114,23,0.45)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
                        onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 4px 16px rgba(248,114,23,0.3)"; e.currentTarget.style.transform = "translateY(0)"; }}
                    >
                        Generate Executive Summary <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </button>
                    <button
                        type="button" onClick={resetAll}
                        className="flex items-center gap-1.5 rounded-xl px-6 py-3 text-sm font-semibold transition-all duration-200"
                        style={{ background: isDark ? "rgba(255,255,255,0.05)" : "#f8fafc", border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)"}`, color: isDark ? "#c9d1d9" : "#374151" }}
                        onMouseEnter={e => hover(e, true)}
                        onMouseLeave={e => hover(e, false)}
                    >
                        <RotateCcw className="w-4 h-4" /> Clear All
                    </button>
                </div>
 
            </div>
            </form>
        </div>
    );
}
 
// ─── Mini Primitives ──────────────────────────────────────────────────────────
 
 
function MiniStat({ label, value, compact = false, isDark }) {
    return (
        <div className="rounded-xl px-3 py-2" style={{ background: isDark ? "rgba(255,255,255,0.05)" : "#f8fafc", border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.07)"}` }}>
            <p className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: isDark ? "#6e7681" : "#94a3b8" }}>{label}</p>
            <p className={`mt-0.5 font-bold ${compact ? "text-xs" : "text-sm"}`} style={{ color: isDark ? "#f0f6fc" : "#0d1a3a" }}>{value}</p>
        </div>
    );
}
 
function InsightCard({ label, value, isDark }) {
    return (
        <div className="rounded-xl p-3" style={{ border: `1px solid ${isDark ? "rgba(255,255,255,0.07)" : "#e2e8f0"}`, background: isDark ? "rgba(255,255,255,0.03)" : "#fafafa" }}>
            <p className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: isDark ? "#6e7681" : "#94a3b8" }}>{label}</p>
            <p className="mt-1 text-xs font-semibold leading-snug" style={{ color: isDark ? "#c9d1d9" : "#1e293b" }}>{value}</p>
        </div>
    );
}
 
function Input({ label, type = "text", value, onChange, isDark, required = false }) {
    return (
        <label className="block">
            <span className="mb-1 block text-xs font-semibold" style={{ color: isDark ? "#c9d1d9" : "#374151" }}>
                {label}{required && <span style={{ color: "#F87217" }}> *</span>}
            </span>
            <input
                type={type} value={value} onChange={onChange} required={required}
                className="w-full rounded-xl px-4 py-3 text-sm outline-none transition-all duration-200"
                style={{ background: isDark ? "rgba(255,255,255,0.05)" : "#fff", border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "#d1d5db"}`, color: isDark ? "#f0f6fc" : "#0f172a" }}
                onFocus={e  => { e.target.style.borderColor = "#F87217"; e.target.style.boxShadow = "0 0 0 3px rgba(248,114,23,0.12)"; }}
                onBlur={e   => { e.target.style.borderColor = isDark ? "rgba(255,255,255,0.1)" : "#d1d5db"; e.target.style.boxShadow = "none"; }}
            />
        </label>
    );
}
 
export function __testables__() {
    return {
        buildInitialScores,
        getMaturity,
        getRecommendationMap,
        MAX_SCORE,
        pillarCount: PILLARS.length,
        questionCount: PILLARS.reduce((sum, pillar) => sum + pillar.questions.length, 0),
    };
}